import type { FC } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CalendarIcon } from '@/assets';
import { ListItem } from '@/features/list/components/ListItem';
import {
  DATE_FORMAT,
  DATE_TIME_FORMAT
} from '@/features/post-editor/constants/dateTime';
import { PostForm } from '@/features/post-editor/constants/form';
import type { PostFormValues } from '@/features/post-editor/types/postForm';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { getFormattedDate } from '@/shared/utils/date';
import { useAppTheme } from '@/styles/hooks/useAppTheme';

type Props = {
  onPress: () => void;
};

/** Editor row showing the selected happening date range. */
export const PostDateField: FC<Props> = ({ onPress }) => {
  const theme = useAppTheme();
  const { t } = useTranslation(LocalizationNameSpace.POST_CREATION, {
    keyPrefix: 'screens.post-editor'
  });
  const { control } = useFormContext<PostFormValues>();

  const [allDay, startDate, endDate] = useWatch({
    control,
    name: ['allDay', 'startDate', 'endDate']
  });

  const format = allDay ? DATE_FORMAT : DATE_TIME_FORMAT;
  const dateString = `${getFormattedDate(startDate, format)} - ${getFormattedDate(endDate, format)}`;

  return (
    <ListItem
      testID="post-creation-date-field"
      title={t(PostForm.DATE)}
      badge={dateString}
      icon={() => (
        <CalendarIcon
          fill={theme.colors.onSurfaceVariant}
          width={24}
          height={24}
        />
      )}
      onPress={onPress}
    />
  );
};
